// Alkoholprovet — träningsfrågor (DEMO/draft).
// OBS: Detta är pedagogiska träningsfrågor, INTE kommunens provfrågor.

export type Category =
  | "Alkohollagen"
  | "Alkoholpolitik"
  | "Serveringstillstånd"
  | "Serveringsansvar"
  | "Tillsyn"
  | "Ålderskontroll"
  | "Ordning och nykterhet"
  | "Serveringstider"
  | "Egenkontroll"
  | "Catering"
  | "Folköl"
  | "Gårdsförsäljning"
  | "Marknadsföring"
  | "Nya regler 2026"
  | "Personalansvar"
  | "Gästhantering"
  | "Provsmakning";

export const CATEGORIES: Category[] = [
  "Alkohollagen",
  "Alkoholpolitik",
  "Serveringstillstånd",
  "Serveringsansvar",
  "Tillsyn",
  "Ålderskontroll",
  "Ordning och nykterhet",
  "Serveringstider",
  "Egenkontroll",
  "Catering",
  "Folköl",
  "Gårdsförsäljning",
  "Marknadsföring",
  "Nya regler 2026",
  "Personalansvar",
  "Gästhantering",
  "Provsmakning",
];

export type Question = {
  id: string;
  category: Category;
  question: string;
  options: string[];
  // `correct` and `explanation` are only known after server-side grading.
  // They remain optional on the client; the seed data below keeps them for
  // local fallback usage only.
  correct?: number;
  explanation?: string;
  draft?: boolean;
};


// 10 demo-frågor. Markerade som draft.
export const QUESTIONS: Question[] = [
  {
    id: "q1",
    category: "Ålderskontroll",
    question: "Vilken är lägsta åldern för att få serveras alkoholdrycker på en restaurang med serveringstillstånd?",
    options: ["16 år", "18 år", "20 år", "21 år"],
    correct: 1,
    explanation: "Enligt alkohollagen får alkoholdrycker serveras till personer som fyllt 18 år på serveringsställe med serveringstillstånd.",
    draft: true,
  },
  {
    id: "q2",
    category: "Serveringstillstånd",
    question: "Vem beviljar serveringstillstånd för alkohol i Sverige?",
    options: ["Polismyndigheten", "Folkhälsomyndigheten", "Kommunen", "Skatteverket"],
    correct: 2,
    explanation: "Det är kommunen där serveringsstället ligger som prövar och beviljar serveringstillstånd.",
    draft: true,
  },
  {
    id: "q3",
    category: "Serveringstider",
    question: "Vad är huvudregeln för serveringstidens slut enligt alkohollagen?",
    options: ["Klockan 22:00", "Klockan 01:00", "Klockan 03:00", "Klockan 05:00"],
    correct: 1,
    explanation: "Huvudregeln är att servering ska upphöra senast klockan 01:00, om inte kommunen beslutar annat.",
    draft: true,
  },
  {
    id: "q4",
    category: "Ordning och nykterhet",
    question: "Vad ska personalen göra med en gäst som är märkbart påverkad?",
    options: [
      "Servera en lättare dryck",
      "Avvisa gästen och inte servera mer alkohol",
      "Ringa polisen direkt",
      "Ignorera situationen",
    ],
    correct: 1,
    explanation: "Märkbart påverkade gäster får inte serveras mer alkohol och ska avvisas på ett respektfullt sätt.",
    draft: true,
  },
  {
    id: "q5",
    category: "Serveringsansvar",
    question: "Vem är ytterst ansvarig för att alkohollagen följs på serveringsstället?",
    options: ["Bartendern", "Serveringsansvarig person", "Tillståndshavaren", "Kommunen"],
    correct: 2,
    explanation: "Tillståndshavaren har det yttersta ansvaret. En serveringsansvarig ska finnas på plats under serveringstid.",
    draft: true,
  },
  {
    id: "q6",
    category: "Egenkontroll",
    question: "Vad ska ett egenkontrollprogram främst innehålla?",
    options: [
      "Endast personalens scheman",
      "Rutiner för att säkerställa att alkohollagen följs",
      "Restaurangens meny",
      "Räkenskaper och bokföring",
    ],
    correct: 1,
    explanation: "Egenkontrollprogrammet beskriver rutiner för ålderskontroll, nykterhet, ordning, marknadsföring m.m.",
    draft: true,
  },
  {
    id: "q7",
    category: "Marknadsföring",
    question: "Vilken princip gäller för marknadsföring av alkoholdrycker till konsumenter?",
    options: ["Aggressiv marknadsföring tillåten", "Särskild måttfullhet", "Endast på TV", "Förbjudet helt"],
    correct: 1,
    explanation: "Marknadsföring av alkoholdrycker ska präglas av särskild måttfullhet och får inte vara påträngande.",
    draft: true,
  },
  {
    id: "q8",
    category: "Folköl",
    question: "Vad krävs för att få sälja folköl i en livsmedelsbutik?",
    options: [
      "Inget — fri försäljning",
      "Anmälan till kommunen och egenkontrollprogram",
      "Serveringstillstånd",
      "Tillstånd från Systembolaget",
    ],
    correct: 1,
    explanation: "Försäljning av folköl kräver anmälan till kommunen, lämpliga lokaler, matvarusortiment och egenkontroll.",
    draft: true,
  },
  {
    id: "q9",
    category: "Tillsyn",
    question: "Vilka myndigheter utövar tillsyn över serveringsställen?",
    options: [
      "Endast kommunen",
      "Endast polisen",
      "Kommunen och polismyndigheten",
      "Skatteverket",
    ],
    correct: 2,
    explanation: "Både kommunen och polismyndigheten har tillsynsansvar. Länsstyrelsen utövar regional tillsyn.",
    draft: true,
  },
  {
    id: "q10",
    category: "Nya regler 2026",
    question: "Vilket område har varit aktuellt för regelförändringar inför 2026?",
    options: [
      "Gårdsförsäljning av alkohol",
      "Förbud mot all servering",
      "Sänkt åldersgräns",
      "Avskaffande av serveringstillstånd",
    ],
    correct: 0,
    explanation: "Gårdsförsäljning av alkoholdrycker har varit en återkommande fråga i lagstiftningsarbetet inför 2026.",
    draft: true,
  },
];

export function getQuestionsByCategory(cat?: Category) {
  return cat ? QUESTIONS.filter((q) => q.category === cat) : QUESTIONS;
}
